import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Star, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Pro-reviews-Customer modal.
 * Shown as a button on completed bookings in ProviderDashboard.
 */
export default function RateCustomerDialog({ booking, open, onClose, onDone }) {
    const [rating, setRating] = useState(5);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);

    if (!open || !booking) return null;

    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/bookings/${booking.id}/customer-review`, {
                rating,
                comment: comment.trim(),
            });
            toast.success(`Thanks — ${rating}-star rating saved for ${booking.customer_name}.`);
            onDone?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="rate-customer-dialog"
            className="fixed inset-0 z-[80] bg-black/70 grid place-items-center p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-2xl border border-[#FFD700]/40 bg-background p-6"
            >
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.26em] text-amber-300/90">Rate the Customer</div>
                        <h3 className="font-serif text-2xl text-foreground mt-1">
                            {booking.customer_name}
                        </h3>
                        <div className="text-xs text-muted-foreground mt-1">{booking.service_name}</div>
                    </div>
                    <button
                        data-testid="rate-customer-close"
                        onClick={onClose}
                        className="w-8 h-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 grid place-items-center"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="flex items-center gap-1 mb-5" data-testid="rate-customer-stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            data-testid={`rate-customer-star-${n}`}
                            onClick={() => setRating(n)}
                            className={`w-11 h-11 rounded-md grid place-items-center transition-all ${
                                n <= rating
                                    ? "text-[#FFD700] scale-110"
                                    : "text-white/15 hover:text-white/40"
                            }`}
                            aria-label={`${n} stars`}
                        >
                            <Star
                                size={28}
                                fill={n <= rating ? "#FFD700" : "transparent"}
                                strokeWidth={1.5}
                            />
                        </button>
                    ))}
                </div>

                <textarea
                    data-testid="rate-customer-comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Optional — behaviour, communication, access, cleanliness..."
                    maxLength={500}
                    rows={3}
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-[#FFD700]/60 focus:outline-none resize-none"
                />

                <div className="mt-5 flex gap-3">
                    <button
                        data-testid="rate-customer-cancel"
                        onClick={onClose}
                        className="flex-1 px-4 py-3 rounded-full border border-white/10 text-xs uppercase tracking-[0.22em] text-muted-foreground hover:bg-white/5 transition"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="rate-customer-submit"
                        disabled={saving}
                        onClick={submit}
                        className="flex-1 px-4 py-3 rounded-full bg-[#FFD700] text-black text-xs uppercase tracking-[0.22em] font-semibold hover:bg-amber-300 disabled:opacity-50 transition"
                    >
                        {saving ? "Saving..." : "Submit rating"}
                    </button>
                </div>
            </div>
        </div>
    );
}
